import { ArrowRight, Settings } from "lucide-react"
import type { ReactNode } from "react"
import { cn } from "@/lib/utils"
import { Shimmer } from "../Shimmer/Shimmer"

export interface ChartPaneProps {
  collapsed?: boolean
  chartCollapsed?: boolean
  isEmpty?: boolean
  starting?: boolean
  configBarSlot?: ReactNode
  chartSlot?: ReactNode
  replayBarSlot?: ReactNode
  emptyMessage?: ReactNode
  className?: string
}

export function ChartPane({
  collapsed = false,
  chartCollapsed = false,
  isEmpty = false,
  starting = false,
  configBarSlot,
  chartSlot,
  replayBarSlot,
  emptyMessage,
  className,
}: ChartPaneProps) {
  if (collapsed) {
    return (
      <div className={cn("h-10 flex items-center px-3 text-caption text-muted-foreground", className)}>
        Chart collapsed - drag handle or double-click to restore
      </div>
    )
  }

  if (chartCollapsed) {
    return (
      <div className={cn("shrink-0 h-full overflow-hidden", className)}>
        {isEmpty ? <ChartConfigBarSkeleton /> : configBarSlot}
      </div>
    )
  }

  if (isEmpty) {
    return (
      <div className={cn("flex-1 flex flex-col min-h-0", className)}>
        <ChartConfigBarSkeleton />
        <div className="flex-1 flex flex-col min-h-0 relative">
          <div
            aria-hidden="true"
            className="flex-1 flex flex-col min-h-0 pointer-events-none select-none [filter:blur(3px)_saturate(0.85)]"
          >
            <div className="flex-1 min-h-0 bg-background">{chartSlot}</div>
            {replayBarSlot}
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 bg-background/30 text-center">
            {emptyMessage ?? (
              <p className="text-body-strong text-foreground leading-relaxed">
                Describe your strategy and
                <br />
                AI will build and backtest it here.
                <ArrowRight
                  size={14}
                  strokeWidth={1.5}
                  className="hidden min-[1100px]:inline-block align-middle ml-1"
                />
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("flex-1 flex flex-col min-h-0", className)}>
      {configBarSlot}
      <div className="flex-1 min-h-0 bg-background">
        {starting ? <div className="h-full" /> : chartSlot}
      </div>
      {replayBarSlot}
    </div>
  )
}

function ChartConfigBarSkeleton() {
  return (
    <div className="flex items-center gap-3 px-4 border-b border-border shrink-0 min-h-11 py-2 bg-background">
      <div className="flex items-center gap-3 w-full min-[901px]:hidden">
        <span className="inline-flex items-center text-muted-foreground shrink-0">
          <Settings size={12} strokeWidth={1.75} />
        </span>
        <Shimmer className="h-6 w-12 rounded-md shrink-0" />
        <div className="flex-1 min-w-0 flex items-center">
          <Shimmer className="h-4 w-28" />
        </div>
      </div>

      <div className="hidden min-[901px]:flex items-center gap-3 w-full">
        <span className="inline-flex items-center text-muted-foreground shrink-0">
          <Settings size={12} strokeWidth={1.75} />
        </span>
        <div className="flex items-center gap-x-3 gap-y-1.5 min-w-0 flex-1 flex-wrap">
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="font-medium text-muted-foreground text-caption">Tick Data</span>
            <span className="font-semibold text-eyebrow px-1.5 py-0.5 rounded bg-muted border border-border text-muted-foreground">
              PRO
            </span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-caption text-muted-foreground">Market</span>
            <Shimmer className="h-3 w-14" />
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-caption text-muted-foreground">Range</span>
            <Shimmer className="h-3 w-[70px]" />
            <ArrowRight size={10} strokeWidth={2} className="text-muted-foreground" />
            <Shimmer className="h-3 w-[70px]" />
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-caption text-muted-foreground">Timeframe</span>
            <Shimmer className="h-6 w-12 rounded-md" />
          </div>
        </div>
        <Shimmer className="h-5 w-28 shrink-0" />
      </div>
    </div>
  )
}
